import {
  getCanonicalCheckoutSelection,
  INTERVIEW_COPILOT_ONLY_PLAN_ID,
  PRICING_DATA,
  type BillingInterval,
} from "@/lib/billing/pricing";

export { INTERVIEW_COPILOT_ONLY_PLAN_ID };

export const INTERVIEW_COPILOT_ONLY_MIN_HOURS =
  PRICING_DATA.interviewCopilotOnly.minimumHours;
export const INTERVIEW_COPILOT_ONLY_MAX_HOURS =
  PRICING_DATA.interviewCopilotOnly.maximumHours;
export const INTERVIEW_COPILOT_ONLY_DEFAULT_HOURS =
  PRICING_DATA.interviewCopilotOnly.defaultHours;

export const INTERVIEW_COPILOT_TERM_MONTHS = {
  monthly: 1,
  quarterly: 3,
  semiannual: 6,
} as const;

export const INTERVIEW_COPILOT_REGULAR_RATE_USD = {
  monthly: PRICING_DATA.interviewCopilotOnly.unitPrices.monthly.regular,
  quarterly: PRICING_DATA.interviewCopilotOnly.unitPrices.quarterly.regular,
  semiannual: PRICING_DATA.interviewCopilotOnly.unitPrices.semiannual.regular,
} as const;

export const INTERVIEW_COPILOT_PROMO_RATE_USD = {
  monthly: PRICING_DATA.interviewCopilotOnly.unitPrices.monthly.promo,
  quarterly: PRICING_DATA.interviewCopilotOnly.unitPrices.quarterly.promo,
  semiannual: PRICING_DATA.interviewCopilotOnly.unitPrices.semiannual.promo,
} as const;

export type InterviewCopilotBillingInterval = BillingInterval;

export interface InterviewCopilotOnlyPrice {
  interval: InterviewCopilotBillingInterval;
  hoursPerMonth: number;
  monthlyMinutes: number;
  termMonths: number;
  termHours: number;
  stripeQuantity: number;
  regularRateUsd: number;
  promoRateUsd: number;
  regularSubtotalUsd: number;
  subtotalUsd: number;
  savingsPercent: number;
}

export function isInterviewCopilotBillingInterval(
  value: unknown
): value is InterviewCopilotBillingInterval {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(INTERVIEW_COPILOT_TERM_MONTHS, value)
  );
}

export function normalizeInterviewCopilotHours(value: unknown): number | null {
  const hours =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim()
        ? Number(value)
        : Number.NaN;

  if (
    !Number.isInteger(hours) ||
    hours < INTERVIEW_COPILOT_ONLY_MIN_HOURS ||
    hours > INTERVIEW_COPILOT_ONLY_MAX_HOURS
  ) {
    return null;
  }

  return hours;
}

export function calculateInterviewCopilotOnlyPrice(
  interval: InterviewCopilotBillingInterval,
  hoursValue: unknown,
  usePromo = true
): InterviewCopilotOnlyPrice {
  const hoursPerMonth = normalizeInterviewCopilotHours(hoursValue);
  if (hoursPerMonth === null) {
    throw new Error(
      `Interview Assistant hours must be a whole number between ${INTERVIEW_COPILOT_ONLY_MIN_HOURS} and ${INTERVIEW_COPILOT_ONLY_MAX_HOURS}.`
    );
  }

  const canonical = getCanonicalCheckoutSelection(
    INTERVIEW_COPILOT_ONLY_PLAN_ID,
    interval,
    hoursPerMonth,
  );
  const termMonths = canonical.termMonths;
  const termHours = canonical.prepaidTermHours!;
  const regularRateUsd = INTERVIEW_COPILOT_REGULAR_RATE_USD[interval];
  const promoRateUsd = usePromo
    ? INTERVIEW_COPILOT_PROMO_RATE_USD[interval]
    : regularRateUsd;
  const regularSubtotalUsd = canonical.regularAmountUsd;
  const subtotalUsd = usePromo
    ? canonical.amountUsd
    : canonical.regularAmountUsd;
  const savingsPercent = usePromo ? PRICING_DATA.promoPercent : 0;

  return {
    interval,
    hoursPerMonth,
    monthlyMinutes: hoursPerMonth * 60,
    termMonths,
    termHours,
    stripeQuantity: hoursPerMonth,
    regularRateUsd,
    promoRateUsd,
    regularSubtotalUsd,
    subtotalUsd,
    savingsPercent,
  };
}
